<script lang="ts" setup>
import { useResponsive } from "@composables"
import { TRACKING_EVENTS } from "@constant"
import { UsageEnergy } from "@machine/form.machine"
import { TrackingPlugin } from "@plugins/tracking.plugin"
import { useFormStore } from "@stores/form.store"
import { toTypedSchema } from "@vee-validate/yup"
import { useField, useForm } from "vee-validate"
import { inject, onActivated, onDeactivated, ref } from "vue"
import { array, object, string } from "yup"

const { snapshot, send } = useFormStore()

const { isMobile } = useResponsive()

const { $track } = inject<TrackingPlugin>("tracking")!

const options = [
    { label: "Chauffage", value: "chauffage" },
    { label: "Eau chaude", value: "eau_chaude" },
    { label: "Cuisson", value: "cuisson" },
    { label: "Autres usages", value: "autres_usages" },
] as { label: string; value: UsageEnergy }[]

// 🧾 Schéma de validation
const { errors, handleSubmit } = useForm({
    validationSchema: toTypedSchema(
        object({
            usage_energie_gaz_avant_travaux: array()
                .of(string())
                .when("$has_gaz_before", {
                    is: true,
                    then: (schema) =>
                        schema
                            .min(
                                1,
                                "Sélectionnez au moins un type d'usage de votre gaz avant travaux"
                            )
                            .required(
                                "Sélectionnez un usage de votre gaz avant travaux"
                            ),
                })
                .default(
                    snapshot.value.context.usage_energie_gaz_avant_travaux
                ),
            usage_energie_gaz_apres_travaux: array()
                .of(string())

                .when("$has_gaz_after", {
                    is: true,
                    then: (schema) =>
                        schema
                            .min(
                                1,
                                "Sélectionnez au moins un type d'usage de votre gaz après travaux"
                            )
                            .required(
                                "Sélectionnez un usage de votre gaz après travaux"
                            ),
                })

                .default(
                    snapshot.value.context.usage_energie_gaz_apres_travaux
                ),
        }),
        {
            context: {
                has_gaz_before:
                    snapshot.value.context.type_energie_avant_travaux.includes(
                        "gaz"
                    ),
                has_gaz_after:
                    snapshot.value.context.type_energie_apres_travaux.includes(
                        "gaz"
                    ),
            },
        }
    ),
})

function onError() {
    $track(TRACKING_EVENTS.FORM_INAVLID, {
        formId: snapshot.value.context.formId,
        step: snapshot.value.context.step + 1,
    })
}

function onSuccess(values: any) {
    // Met à jour le contexte de la machine
    send({
        type: "UPDATE_FIELD",
        field: "usage_energie_gaz_avant_travaux",
        value: values.usage_energie_gaz_avant_travaux,
    })
    send({
        type: "UPDATE_FIELD",
        field: "usage_energie_gaz_apres_travaux",
        value: values.usage_energie_gaz_apres_travaux,
    })

    send({
        type: "SUBMIT",
    })
}

const onSubmit = handleSubmit(onSuccess, onError)

const { value: usage_energie_gaz_avant_travaux } = useField(
    "usage_energie_gaz_avant_travaux"
)

const { value: usage_energie_gaz_apres_travaux } = useField(
    "usage_energie_gaz_apres_travaux"
)

const timeCompletion = ref(new Date().getTime())

onActivated(() => {
    timeCompletion.value = new Date().getTime()
    $track(TRACKING_EVENTS.STEP_ENTERED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
    })
})

onDeactivated(() => {
    $track(TRACKING_EVENTS.STEP_COMPLETED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
        timeCompletion: (
            (new Date().getTime() - timeCompletion.value) /
            1000
        ).toFixed(1),
    })
})
</script>

<template>
    <FormCard
        class="form-energy-gaz"
        title="L'essentiel"
        sub-title="Gaz : Usages avant et après travaux"
        @submit="
            () => {
                onSubmit()
                $track(TRACKING_EVENTS.CLICK_BUTTON, {
                    buttonId: 'submit-form-energy-gaz',
                    buttonName: 'Suivant',
                })
            }
        "
        @back="
            () => {
                send({ type: 'BACK' })
                $track(TRACKING_EVENTS.CLICK_BUTTON, {
                    buttonId: 'back-form-energy-gaz',
                    buttonName: 'Retour',
                })
            }
        "
        :loading-submit="
            snapshot.matches({ Step2: { FormEnergyGaz: 'updateField' } })
        "
    >
        <!-- Avant travaux -->
        <fieldset
            v-if="snapshot.context.type_energie_avant_travaux.includes('gaz')"
        >
            <legend class="text-primary-800 font-bold mb-4">
                <span class="underline">Avant travaux</span>, vous vous serviez
                du gaz pour quels usages ?
            </legend>
            <BaseCheckboxList
                input-id="usage_energie_gaz_avant_travaux"
                :options="options"
                name="usage_energie_gaz_avant_travaux"
                option-label="label"
                option-value="value"
                v-model="usage_energie_gaz_avant_travaux"
                :horizontal="!isMobile"
                :error="errors.usage_energie_gaz_avant_travaux"
                :invalid="!!errors.usage_energie_gaz_avant_travaux"
            />
        </fieldset>
        <!-- Après travaux -->
        <fieldset
            v-if="snapshot.context.type_energie_apres_travaux.includes('gaz')"
        >
            <legend class="text-primary-800 font-bold mb-4">
                <span class="underline">Après travaux</span>, vous vous servez
                du gaz pour quels usages ?
            </legend>
            <BaseCheckboxList
                input-id="usage_energie_gaz_apres_travaux"
                :options="options"
                name="usage_energie_gaz_apres_travaux"
                option-label="label"
                option-value="value"
                v-model="usage_energie_gaz_apres_travaux"
                :horizontal="!isMobile"
                :error="errors.usage_energie_gaz_apres_travaux"
                :invalid="!!errors.usage_energie_gaz_apres_travaux"
            />
        </fieldset>
    </FormCard>
</template>

<style scoped>
.form-energy-gaz {
    --gap-form: 3.5rem;
    --padding-form: 3.5rem;
    &:deep(.p-card-content) {
        display: flex;
        flex-direction: column;
        gap: var(--gap-form);
        padding-top: var(--padding-form);

        @media (--small-viewport) {
            --gap-form: 2.5rem;
            --padding-form: 2.5rem;
        }
    }
}
</style>
